import { APP_BASE_HREF, CommonModule, TitleCasePipe } from '@angular/common';
import {
  HTTP_INTERCEPTORS,
  provideHttpClient,
  withInterceptorsFromDi,
  withJsonpSupport,
  withXhr,
} from '@angular/common/http';
import {
  APP_ID,
  inject,
  LOCALE_ID,
  NgModule,
  provideAppInitializer,
} from '@angular/core';
import { BrowserModule } from '@angular/platform-browser';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';

// third-party
import { NgxSliderModule } from '@angular-slider/ngx-slider';
import {
  AssessContentActions,
  AssessContentImage,
  AssessContentSkills,
  AssessTranslateLoader,
  provideAssessAdapters,
} from '@degreed/lxp/assess/services';
import { NgbModalModule } from '@ng-bootstrap/ng-bootstrap';
import {
  TranslateLoader,
  TranslateModule,
  TranslateService,
} from '@ngx-translate/core';

// misc
import { initAppState } from '@app/app-initializer';
import {
  CachingInterceptor,
  CamelCasingInterceptor,
  ErrorHandlingInterceptor,
  MiniProfilerInterceptor,
  ORG_TYPE_TOKEN,
  WindowToken,
  XSRFTokenInterceptor,
} from '@degreed/lxp/services';
// modules

import { ContentHostingDataAccessModule } from '@app/content-hosting/data-access/content-hosting-data-access.module';
import { AppRoutingModule } from '@app/routing/app-routing.module';
import { SharedProvidersModule } from '@app/shared/shared-providers.module';

import { TranslationModule } from '@app/translation/translation.module';

// services
import {
  ConfigurationService,
  LocalizationService,
  WebEnvironmentService,
} from '@degreed/lxp/services';
import {
  AuthInterceptor,
  AuthService,
} from '@degreed/lxp/services/authorization';

import { ApolloLayoutProvider } from '@app/layout';

// components
import { AppComponent } from '@app/app.component';
import { LDFlagsService } from '@degreed/lxp/services';
import { LANGUAGE_OPTIONS_RESOLVER } from '@degreed/lxp/services/content';
import { GROUP_SEARCH_RESOLVER } from '@degreed/lxp/ui/content';
import { AnalyticsGuard } from './analytics/guards/analytics.guard';
import { LxpAssessContentActionsAdapter } from './content-catalog/services/assess-content-actions.adapter';
import { LxpAssessContentImageAdapter } from './content-catalog/services/assess-content-image.adapter';
import { LxpAssessContentSkillsAdapter } from './content-catalog/services/assess-content-skills.adapter';
import { CatalogGroupSearchResolverService } from './content-catalog/services/catalog-group-search-resolver.service';
import { LanguageOptionsResolverService } from './content-catalog/services/language-options-resolver.service';
import { provideGdAccess } from './reporting/analytics-studio-access/gd-access.providers';

import {
  ApplicationId,
  BrandingService,
  VendorIntegrationsService,
} from '@degreed/lxp/services';

import {
  LuxonDateAdapter,
  MAT_LUXON_DATE_FORMATS,
  MatLuxonDateModule,
} from '@angular/material-luxon-adapter';
import {
  DateAdapter,
  MAT_DATE_FORMATS,
  MAT_DATE_LOCALE,
} from '@angular/material/core';
import { MatDatepickerIntl } from '@angular/material/datepicker';
import { DatepickerIntl } from '@degreed/apollo';
import { TINYMCE_BASE_URL } from '@degreed/tinymce';

@NgModule({
  declarations: [AppComponent],
  bootstrap: [AppComponent],
  imports: [
    // temporary until we're out of non apollo datepickers
    MatLuxonDateModule, // required for Luxon support
    /* Angular modules */
    BrowserModule,
    CommonModule,
    BrowserAnimationsModule,
    /* Third-party modules */
    NgbModalModule,
    NgxSliderModule,
    TranslateModule.forRoot({
      loader: {
        provide: TranslateLoader,
        // assess: temporary (D-24) — revert to DgTranslateDynamicLoader at the backend move
        useClass: AssessTranslateLoader,
      },
    }),
    SharedProvidersModule,
    TranslationModule,
    AppRoutingModule,
    ContentHostingDataAccessModule,
  ],
  providers: [
    // assess: AssessHttp → HttpClientAssessHttp over /api/assess (P-16);
    // AssessNotifier → lxp notifier
    ...provideAssessAdapters(),
    // assess: AssessContentActions → the app-only Share/Add to Pathway/Add to
    // Job Role flows (content-catalog-menu.service.ts's app-only equivalents)
    { provide: AssessContentActions, useClass: LxpAssessContentActionsAdapter },
    // assess: AssessContentSkills → the app-only localized/non-localized split
    // (catalog-content API vs. the legacy assessment edit flow)
    { provide: AssessContentSkills, useClass: LxpAssessContentSkillsAdapter },
    // assess: AssessContentImage → same localized/non-localized split, for the
    // assessment's cover image (imageUrl/resourceImageId)
    { provide: AssessContentImage, useClass: LxpAssessContentImageAdapter },
    // assess: the add-content drawer renders under the Apollo DrawerService's
    // root injector, so route-level providers (orgs-routing.module.ts) aren't
    // visible to dgx-content-language-field inside it; provide it here too.
    {
      provide: LANGUAGE_OPTIONS_RESOLVER,
      useExisting: LanguageOptionsResolverService,
    },
    // external content: its edit drawer also renders under the DrawerService's
    // root injector, so dgx-content-visibility-field needs the group search here.
    {
      provide: GROUP_SEARCH_RESOLVER,
      useClass: CatalogGroupSearchResolverService,
    },
    // translation
    {
      provide: ORG_TYPE_TOKEN,
      useFactory: (authService: AuthService) => () =>
        authService.authUser?.defaultOrgInfo?.type,
      deps: [AuthService],
    },
    // Analytics Studio: GD_ACCESS at root so the scheduled-export drawer (root injector) sees it too
    provideGdAccess(),
    // interceptors
    { provide: HTTP_INTERCEPTORS, useClass: XSRFTokenInterceptor, multi: true },
    { provide: HTTP_INTERCEPTORS, useClass: CachingInterceptor, multi: true },
    { provide: HTTP_INTERCEPTORS, useClass: AuthInterceptor, multi: true },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: CamelCasingInterceptor,
      multi: true,
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: ErrorHandlingInterceptor,
      multi: true,
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: MiniProfilerInterceptor,
      multi: true,
    },

    // luxon specific - remove once we've removed /shared/dgx-datepicker etc..
    {
      provide: DateAdapter,
      useClass: LuxonDateAdapter,
      deps: [MAT_DATE_LOCALE],
    },
    { provide: MAT_DATE_FORMATS, useValue: MAT_LUXON_DATE_FORMATS },
    { provide: MatDatepickerIntl, useClass: DatepickerIntl },

    // non localized date pickers
    { provide: MAT_DATE_LOCALE, useValue: 'en-US' },

    // when we're ready for localized date pickers uncomment
    // {
    //   provide: MAT_DATE_LOCALE,
    //   useFactory: (localizationService: LocalizationService) =>
    //     localizationService.userLocale,
    //   deps: [LocalizationService],
    // },
    // end luxon specific

    TitleCasePipe,
    AnalyticsGuard,
    {
      provide: LOCALE_ID,
      deps: [LocalizationService],
      useFactory: (localizationService: LocalizationService) => {
        const { userLocale } = localizationService;
        // lazy-load the locales which we need for localizing dates in some areas of the app
        localizationService.loadLocale(userLocale);
        return userLocale;
      },
    },
    {
      provide: APP_BASE_HREF,
      useFactory: (win: Window) => win.origin,
      deps: [WindowToken],
    },
    provideAppInitializer(() => {
      const initializerFn = initAppState(
        inject(WebEnvironmentService),
        inject(AuthService),
        inject(TranslateService),
        inject(ConfigurationService),
        inject(LocalizationService),
        inject(LDFlagsService),
        inject(BrandingService),
        inject(VendorIntegrationsService)
      );
      return initializerFn();
    }),
    {
      provide: APP_ID,
      useValue: ApplicationId.LXP,
    },
    ApolloLayoutProvider,
    // dgx-rich-text-editor loads its TinyMCE skins and plugins from blob storage
    {
      provide: TINYMCE_BASE_URL,
      useFactory: () =>
        `${inject(WebEnvironmentService).getBlobBaseUrl()}/tinymce`,
    },

    provideHttpClient(withXhr(), withInterceptorsFromDi(), withJsonpSupport()),
  ],
})
export class AppModule {}
